import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../Common/Hooks/useAuth";
import { Input } from "../../Components/Input";
import { Notification } from "../../Components/Notifications";
import { eventBus } from "../../Common/EventBus";
import { ErrorMessage } from "../../Components/ErrorMessage";
import { LoginFormWrapper, NavigateParagraph, LoginButton } from "../Login/Styles";
import { validateEmail } from "../../Common/Utils/Validators";
import { isErr } from "../../Common/Utils/Result";
import { FaRegUserCircle } from "react-icons/fa";
import { RotatingLoaderIcon } from "../../Components/Button/Styles";
import { HiArrowRight } from "react-icons/hi2";

interface ResetPasswordRequestProps {
  backCallback: () => void;
  backToLoginCallback: () => void;
}

export const ResetPasswordRequest: React.FC<ResetPasswordRequestProps> = ({ backCallback, backToLoginCallback }) => {
  const { t } = useTranslation();
  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState("");
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const { requestPasswordReset } = useAuth();

  const handleSubmit = async () => {
    if (!validateEmail(email)) {
      setEmailError(t("auth.resetPasswordRequest.errors.emailInvalid"));
      return;
    }

    setLoading(true);
    const response = await requestPasswordReset(email);
    setLoading(false);

    if (isErr(response)) {
      eventBus.emit(
        "NOTIFICATION",
        Notification.buildNotification("error", t("auth.resetPasswordRequest.errors.requestFailed"))
      );
      return;
    }

    setSubmitted(true);
  };

  return (
    <LoginFormWrapper>
      {!submitted ? (
        <>
          <h1 style={{ margin: 0, marginBottom: "1rem", textAlign: "center" }}>{t("auth.resetPasswordRequest.title")}</h1>
          <p style={{ textAlign: "center", marginBottom: "1rem", color: "#565869" }}>
            {t("auth.resetPasswordRequest.subtitle")}
          </p>
          <Input
            Icon={FaRegUserCircle}
            placeholder={t("auth.resetPasswordRequest.emailPlaceholder")}
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            error={!!emailError}
            onEnterPress={handleSubmit}
          />
          <ErrorMessage errorMessage={emailError} />
          <LoginButton onClick={handleSubmit} disabled={loading} $isLoading={loading}>
            {loading && <RotatingLoaderIcon />}
            {loading ? t("auth.resetPasswordRequest.submitButtonLoading") : (
              <>
                <span>{t("auth.resetPasswordRequest.submitButton")}</span>
                <HiArrowRight />
              </>
            )}
          </LoginButton>
          <NavigateParagraph>
            <b onClick={() => backCallback()}>{t("auth.resetPasswordRequest.backButton")}</b>
          </NavigateParagraph>
        </>
      ) : (
        <>
          <h1 style={{ margin: 0, marginBottom: "1rem", textAlign: "center" }}>{t("auth.resetPasswordRequest.submittedTitle")}</h1>
          <p style={{ textAlign: "center", marginBottom: "1rem", color: "#565869" }}>
            {t("auth.resetPasswordRequest.submittedSubtitle", { email })}
          </p>
          <p style={{ textAlign: "center", marginBottom: "1rem", color: "#565869" }}>
            {t("auth.resetPasswordRequest.submittedHint")}
          </p>
          <LoginButton onClick={() => backToLoginCallback()}>
            <span>{t("auth.resetPasswordRequest.continueButton")}</span>
            <HiArrowRight />
          </LoginButton>
        </>
      )}
    </LoginFormWrapper>
  );
};
